import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchUsers,
  setSearchTerm,
  setFilterRole,
  clearError,
} from '../../store/slices/usersSlice';
import { User } from '../../types/auth';
import { useAuth } from '../../contexts/AuthContext';
import UserCreateDialog from '../../components/users/UserCreateDialog';
import UserEditDialog from '../../components/users/UserEditDialog';
import UserDeleteDialog from '../../components/users/UserDeleteDialog';

const UserManagementPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user: currentUser } = useAuth();
  const {
    users,
    isLoading,
    error,
    searchTerm,
    filterRole,
  } = useAppSelector((state) => state.users);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Table states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuUser, setActionMenuUser] = useState<User | null>(null);

  useEffect(() => {
    // Check if user is admin
    if (currentUser?.role !== 'admin') {
      return;
    }

    dispatch(fetchUsers({}));
  }, [dispatch, currentUser]);

  // Filter users based on search term and role filter
  const filteredUsers = useMemo(() => {
    // Ensure users is always an array to prevent errors
    const safeUsers = Array.isArray(users) ? users : [];
    let filtered = safeUsers;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        user =>
          user.username.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower)
      );
    }

    if (filterRole) {
      filtered = filtered.filter(user => user.role === filterRole);
    }

    return filtered;
  }, [users, searchTerm, filterRole]);

  // Paginated users
  const paginatedUsers = useMemo(() => {
    // Ensure filteredUsers is an array before calling slice
    if (!Array.isArray(filteredUsers)) {
      return [];
    }
    const startIndex = page * rowsPerPage;
    return filteredUsers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchTerm(event.target.value));
    setPage(0); // Reset to first page when searching
  };

  const handleRoleFilterChange = (event: any) => {
    dispatch(setFilterRole(event.target.value));
    setPage(0); // Reset to first page when filtering
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuUser(user);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setActionMenuUser(null);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
    handleActionMenuClose();
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
    handleActionMenuClose();
  };

  const handleDialogSuccess = () => {
    dispatch(fetchUsers({}));
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <AdminIcon color="error" />;
      case 'user':
        return <PersonIcon color="primary" />;
      case 'viewer':
        return <ViewIcon color="action" />;
      default:
        return <PersonIcon />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'user':
        return 'Benutzer';
      case 'viewer':
        return 'Betrachter';
      default:
        return role;
    }
  };

  const getRoleColor = (role: string): 'error' | 'primary' | 'default' => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'user':
        return 'primary';
      default:
        return 'default';
    }
  };

  // Check if user is admin
  if (currentUser?.role !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Sie haben keine Berechtigung, auf diese Seite zuzugreifen. Nur Administratoren können Benutzer verwalten.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Benutzerverwaltung
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Neuer Benutzer
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Nach Benutzername oder E-Mail suchen..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Rolle filtern</InputLabel>
            <Select
              value={filterRole}
              onChange={handleRoleFilterChange}
              label="Rolle filtern"
            >
              <MenuItem value="">Alle Rollen</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="user">Benutzer</MenuItem>
              <MenuItem value="viewer">Betrachter</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary">
            {filteredUsers.length} von {users.length} Benutzer{filteredUsers.length !== 1 ? 'n' : ''}
          </Typography>
        </Box>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Benutzer</TableCell>
              <TableCell>E-Mail</TableCell>
              <TableCell>Rolle</TableCell>
              <TableCell>Erstellt am</TableCell>
              <TableCell>Zuletzt aktualisiert</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Lade Benutzer...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body1" color="text.secondary" sx={{ py: 3 }}>
                    {filteredUsers.length === 0 && users.length > 0 
                      ? 'Keine Benutzer entsprechen den Suchkriterien.'
                      : 'Noch keine Benutzer vorhanden.'
                    }
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getRoleIcon(user.role)}
                      <Typography variant="body1" fontWeight="medium">
                        {user.username}
                      </Typography>
                      {user.username === 'admin' && (
                        <Chip label="System" size="small" variant="outlined" color="warning" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={getRoleLabel(user.role)}
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString('de-DE')}
                  </TableCell>
                  <TableCell>
                    {new Date(user.updated_at).toLocaleDateString('de-DE')}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Aktionen">
                      <IconButton
                        onClick={(event) => handleActionMenuOpen(event, user)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        {!isLoading && filteredUsers.length > 0 && (
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Einträge pro Seite:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}–${to} von ${count !== -1 ? count : `mehr als ${to}`}`
            }
          />
        )}
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={() => actionMenuUser && handleEditUser(actionMenuUser)}>
          <EditIcon sx={{ mr: 1 }} />
          Bearbeiten
        </MenuItem>
        <MenuItem
          onClick={() => actionMenuUser && handleDeleteUser(actionMenuUser)}
          disabled={
            actionMenuUser?.username === 'admin' ||
            actionMenuUser?.id === currentUser?.id
          }
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Löschen
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <UserCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />

      <UserEditDialog
        open={editDialogOpen}
        user={selectedUser}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={handleDialogSuccess}
      />

      <UserDeleteDialog
        open={deleteDialogOpen}
        user={selectedUser}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={handleDialogSuccess}
      />
    </Box>
  );
};

export default UserManagementPage;